/**
 * Evidence Timeline Component
 * Shows a chronological timeline of evidence events grouped by stage
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Filter, Clock } from 'lucide-react';
import { EvidenceCard } from './EvidenceCard';
import type { Evidence, EvidenceStage, EvidenceType } from '../../../api/evidence';

interface EvidenceTimelineProps {
  evidence: Evidence[];
  groupByStage?: boolean;
  showFilters?: boolean;
}

const STAGE_ORDER: EvidenceStage[] = ['DEVELOPMENT', 'TESTING', 'REVIEW', 'MERGE', 'COMPLETION'];

const STAGE_COLORS: Record<EvidenceStage, string> = {
  DEVELOPMENT: '#8B5CF6',
  TESTING: '#10B981',
  REVIEW: '#F59E0B',
  MERGE: '#EC4899',
  COMPLETION: '#6B7280',
};

export function EvidenceTimeline({ evidence, groupByStage = true, showFilters = true }: EvidenceTimelineProps) {
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set(STAGE_ORDER));
  const [filterStage, setFilterStage] = useState<EvidenceStage | 'all'>('all');
  const [filterType, setFilterType] = useState<EvidenceType | 'all'>('all');
  const [showDetails, setShowDetails] = useState(false);

  // Get unique types from evidence
  const uniqueTypes = Array.from(new Set(evidence.map((e) => e.evidenceType)));

  // Filter evidence
  let filteredEvidence = evidence;
  if (filterStage !== 'all') {
    filteredEvidence = filteredEvidence.filter((e) => e.stage === filterStage);
  }
  if (filterType !== 'all') {
    filteredEvidence = filteredEvidence.filter((e) => e.evidenceType === filterType);
  }

  // Group by stage if requested
  const groupedEvidence = groupByStage
    ? STAGE_ORDER.reduce(
        (acc, stage) => {
          acc[stage] = filteredEvidence.filter((e) => e.stage === stage);
          return acc;
        },
        {} as Record<EvidenceStage, Evidence[]>
      )
    : null;

  const toggleStage = (stage: string) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stage)) {
      newExpanded.delete(stage);
    } else {
      newExpanded.add(stage);
    }
    setExpandedStages(newExpanded);
  };

  if (evidence.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: '#F5F1EC' }}
        >
          <Clock className="w-8 h-8" style={{ color: '#A39686' }} />
        </div>
        <h4 className="font-medium mb-2" style={{ color: '#2F241B' }}>
          No Evidence Yet
        </h4>
        <p className="text-sm" style={{ color: '#6B5D52' }}>
          Evidence will appear here as the issue progresses through stages
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-3 pb-4 border-b" style={{ borderColor: '#E8E0D5' }}>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: '#A39686' }} />
            <span className="text-sm font-medium" style={{ color: '#6B5D52' }}>
              Filters:
            </span>
          </div>

          {/* Stage filter */}
          <select
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value as EvidenceStage | 'all')}
            className="text-sm px-3 py-1.5 rounded-lg border focus:outline-none focus-coral"
            style={{ borderColor: '#E8E0D5', color: '#2F241B', backgroundColor: 'white' }}
          >
            <option value="all">All Stages</option>
            {STAGE_ORDER.map((stage) => (
              <option key={stage} value={stage}>
                {stage}
              </option>
            ))}
          </select>

          {/* Type filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as EvidenceType | 'all')}
            className="text-sm px-3 py-1.5 rounded-lg border focus:outline-none focus-coral"
            style={{ borderColor: '#E8E0D5', color: '#2F241B', backgroundColor: 'white' }}
          >
            <option value="all">All Types</option>
            {uniqueTypes.map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ')}
              </option>
            ))}
          </select>

          {/* Show details toggle */}
          <label className="flex items-center gap-2 text-sm cursor-pointer ml-auto">
            <input
              type="checkbox"
              checked={showDetails}
              onChange={(e) => setShowDetails(e.target.checked)}
              className="rounded focus-coral"
              style={{ color: '#D97F6F' }}
            />
            <span style={{ color: '#6B5D52' }}>Show details</span>
          </label>
        </div>
      )}

      {/* Grouped view */}
      {groupByStage && groupedEvidence && (
        <div className="space-y-4">
          {STAGE_ORDER.map((stage) => {
            const stageEvidence = groupedEvidence[stage];
            if (stageEvidence.length === 0 && filterStage !== 'all') return null;

            const isExpanded = expandedStages.has(stage);
            const stageColor = STAGE_COLORS[stage];

            return (
              <div key={stage} className="rounded-lg border overflow-hidden" style={{ borderColor: '#E8E0D5' }}>
                {/* Stage Header */}
                <button
                  onClick={() => toggleStage(stage)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                  style={{ backgroundColor: stageEvidence.length > 0 ? stageColor + '10' : 'white' }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: stageEvidence.length > 0 ? stageColor : '#E8E0D5' }}
                    />
                    <span className="font-medium" style={{ color: '#2F241B' }}>
                      {stage}
                    </span>
                    <span
                      className="text-sm px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: '#F5F1EC', color: '#6B5D52' }}
                    >
                      {stageEvidence.length}
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5" style={{ color: '#A39686' }} />
                  ) : (
                    <ChevronRight className="w-5 h-5" style={{ color: '#A39686' }} />
                  )}
                </button>

                {/* Stage Content */}
                {isExpanded && (
                  <div className="p-4 space-y-3" style={{ backgroundColor: '#FAFAFA' }}>
                    {stageEvidence.length > 0 ? (
                      stageEvidence
                        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                        .map((item) => <EvidenceCard key={item.eventId} evidence={item} showDetails={showDetails} />)
                    ) : (
                      <p className="text-sm text-center py-4" style={{ color: '#A39686' }}>
                        No evidence for this stage yet
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Flat chronological view */}
      {!groupByStage && (
        <div className="space-y-3">
          {filteredEvidence
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
            .map((item) => (
              <div key={item.eventId} className="flex items-start gap-3">
                {/* Timeline line */}
                <div className="flex flex-col items-center">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: STAGE_COLORS[item.stage as EvidenceStage] || '#A39686' }}
                  />
                  <div className="w-0.5 flex-1 min-h-[20px]" style={{ backgroundColor: '#E8E0D5' }} />
                </div>
                {/* Card */}
                <div className="flex-1 pb-4">
                  <EvidenceCard evidence={item} showDetails={showDetails} />
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default EvidenceTimeline;
